import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Chip, IconButton, Collapse, Skeleton,
  TextField, InputAdornment, Paper, Divider, Card, FormControl, Select, MenuItem
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InboxIcon from '@mui/icons-material/Inbox';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { fetchRecords } from '../services/api';

const fmtCurrency = (val, cur = 'INR') => {
  const num = Number(val);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(num);
};

export default function BalanceValueGrouped({ records, onEdit, onView, onAdd }) {
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [search, setSearch] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  
  const [assignments, setAssignments] = useState([]);
  const [persons, setPersons] = useState([]);
  const [runs, setRuns] = useState([]);
  const [definedBalances, setDefinedBalances] = useState([]);
  const [balanceDefs, setBalanceDefs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetchRecords('ASSIGNMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('PERSON').catch(() => ({ data: { data: [] } })),
      fetchRecords('PAYROLL_RUN').catch(() => ({ data: { data: [] } })),
      fetchRecords('DEFINED_BALANCE').catch(() => ({ data: { data: [] } })),
      fetchRecords('BALANCE_DEFINITION').catch(() => ({ data: { data: [] } }))
    ]).then(([a, p, r, db, bdef]) => {
      setAssignments(a.data.data || []);
      setPersons(p.data.data || []);
      setRuns(r.data.data || []);
      setDefinedBalances(db.data.data || []);
      setBalanceDefs(bdef.data.data || []);
      setLoading(false);
    });
  }, []);

  const assignmentMap = useMemo(() => Object.fromEntries(assignments.map(a => [a.assignment_id, a])), [assignments]);
  const personMap = useMemo(() => Object.fromEntries(persons.map(p => [p.person_id, p])), [persons]);
  const runMap = useMemo(() => Object.fromEntries(runs.map(r => [r.run_id, r])), [runs]);
  const definedBalMap = useMemo(() => Object.fromEntries(definedBalances.map(db => [db.defined_balance_id, db])), [definedBalances]);
  const balanceDefMap = useMemo(() => Object.fromEntries(balanceDefs.map(bd => [bd.balance_def_id, bd])), [balanceDefs]);

  const runOptionsWithYear = useMemo(() => {
    const runIds = new Set();
    records.forEach(r => {
      if (r.run_id) runIds.add(r.run_id);
    });
    
    return Array.from(runIds).map(runId => {
      const run = runMap[runId];
      const label = run ? run.run_name || run.period_name || runId : runId;
      let year = new Date().getFullYear().toString();
      const match = label.match(/\b(20\d{2})\b/);
      if (match) {
        year = match[1];
      } else if (run && run.process_date) {
        year = run.process_date.substring(0, 4);
      } else if (run && run.run_date) {
        year = run.run_date.substring(0, 4);
      }
      return { run_id: runId, label, year };
    }).sort((a, b) => b.label.localeCompare(a.label));
  }, [records, runMap]);

  const yearOptions = useMemo(() => {
    return Array.from(new Set(runOptionsWithYear.map(o => o.year))).sort((a,b) => b.localeCompare(a));
  }, [runOptionsWithYear]);

  const filteredMonthOptions = useMemo(() => {
    if (!filterYear) return runOptionsWithYear;
    return runOptionsWithYear.filter(o => o.year === filterYear);
  }, [runOptionsWithYear, filterYear]);

  const [hasInitializedFilter, setHasInitializedFilter] = useState(false);

  useEffect(() => {
    if (!loading && yearOptions.length > 0 && !hasInitializedFilter) {
      const currentY = new Date().getFullYear().toString();
      const defaultYear = yearOptions.includes(currentY) ? currentY : yearOptions[0];
      setFilterYear(defaultYear);
      
      const defaultMonths = runOptionsWithYear.filter(o => o.year === defaultYear);
      if (defaultMonths.length > 0) {
        setFilterMonth(defaultMonths[0].run_id);
        setExpandedMonth(defaultMonths[0].run_id);
      }
      setHasInitializedFilter(true);
    }
  }, [loading, yearOptions, runOptionsWithYear, hasInitializedFilter]);

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setFilterYear(newYear);
    const availableMonths = runOptionsWithYear.filter(o => o.year === newYear);
    if (availableMonths.length > 0) {
      setFilterMonth(availableMonths[0].run_id);
      setExpandedMonth(availableMonths[0].run_id);
    } else {
      setFilterMonth('');
      setExpandedMonth(null);
    }
  };

  const toggleEmployee = (month, empKey) => {
    setExpandedEmployees(prev => ({
      ...prev,
      [`${month}_${empKey}`]: !prev[`${month}_${empKey}`]
    }));
  };

  const monthGroups = useMemo(() => {
    const mGroup = {};
    
    for (const r of records) {
      const mKey = r.run_id || 'Unknown';
      if (!mGroup[mKey]) mGroup[mKey] = [];
      mGroup[mKey].push(r);
    }
    
    const structured = Object.entries(mGroup).map(([mKey, mRecords]) => {
      const eGroup = {};
      let monthTotalGross = 0;
      let monthTotalDed = 0;
      let monthTotalNet = 0;
      for (const r of mRecords) {
        const asnId = r.assignment_id || '—';
        const pId = r.person_id || '—';
        const eKey = `${pId}_${asnId}`;
        
        if (!eGroup[eKey]) {
          const asn = assignmentMap[asnId];
          const person = asn ? personMap[asn.person_id] : personMap[pId];
          const fullName = person ? [person.first_name, person.last_name].filter(Boolean).join(' ') : null;
          
          eGroup[eKey] = {
            eKey,
            person_id: pId,
            assignment_id: asnId,
            fullName: fullName || (asn ? asn.employee_code : 'Unknown Employee'),
            total_gross: 0,
            total_ded: 0,
            total_net: 0,
            entries: []
          };
        }
        
        eGroup[eKey].entries.push(r);
        const amt = Number(r.balance_amount) || 0;
        
        // Resolve true balance name
        const db = definedBalMap[r.defined_balance_id];
        const bDef = db ? balanceDefMap[db.balance_def_id] : null;
        const bName = (bDef ? bDef.balance_name : (r.context_value || '')).toLowerCase();
        
        if (bName.includes('gross')) {
          eGroup[eKey].total_gross += amt;
          monthTotalGross += amt;
        } else if (bName.includes('deduction')) {
          eGroup[eKey].total_ded += amt;
          monthTotalDed += amt;
        } else if (bName.includes('net')) {
          eGroup[eKey].total_net += amt;
          monthTotalNet += amt;
        }
      }
      
      const employees = Object.values(eGroup).sort((a, b) => a.fullName.localeCompare(b.fullName));
      const run = runMap[mKey];
      const monthLabel = run ? run.run_name || run.period_name || mKey : mKey;
      
      return {
        monthId: mKey,
        monthLabel,
        employees,
        stats: { activeEmployees: employees.length, totalGross: monthTotalGross, totalDed: monthTotalDed, totalNet: monthTotalNet }
      };
    });
    
    return structured.sort((a, b) => b.monthLabel.localeCompare(a.monthLabel));
  }, [records, assignmentMap, personMap, runMap]);

  const filteredMonths = useMemo(() => {
    let result = monthGroups;

    if (filterMonth) result = result.filter(m => m.monthId === filterMonth);
    
    result = result.map(m => {
      let filteredEmps = m.employees;
      
      const q = search.trim().toLowerCase();
      if (q) {
        filteredEmps = filteredEmps.map(e => {
          const empHit = 
            e.assignment_id.toLowerCase().includes(q) ||
            e.fullName.toLowerCase().includes(q);
            
          if (empHit) return e;
          
          const matchingEntries = e.entries.filter(entry => {
            const db = definedBalMap[entry.defined_balance_id];
            const bDef = db ? balanceDefMap[db.balance_def_id] : null;
            const bName = bDef ? bDef.balance_name : '';
            return (
              (entry.context_value || '').toLowerCase().includes(q) ||
              (db?.dimension_name || '').toLowerCase().includes(q) ||
              bName.toLowerCase().includes(q)
            );
          });
          
          return matchingEntries.length ? { ...e, entries: matchingEntries } : null;
        }).filter(Boolean);
      }
      
      return { ...m, employees: filteredEmps };
    }).filter(m => m.employees.length > 0);

    return result;
  }, [monthGroups, filterMonth, search, definedBalMap, balanceDefMap]);

  if (loading) {
    return (
      <Box>
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} height={80} sx={{ mb: 2, borderRadius: 2 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search employee, balance name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{
              width: { xs: '100%', sm: 380 },
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white', fontSize: 13, borderRadius: '10px',
                '& fieldset': { borderColor: '#E8EDF5', borderWidth: '1.5px' },
                '&:hover fieldset': { borderColor: '#bbf7d0' },
                '&.Mui-focused fieldset': { borderColor: '#22c55e', borderWidth: '2px' },
              },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94A3B8', fontSize: 17 }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon sx={{ fontSize: 15 }} /></IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          
          <Stack direction="row" spacing={1.5} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 100, bgcolor: 'white', borderRadius: '8px' }}>
              <Select
                displayEmpty
                value={filterYear}
                onChange={handleYearChange}
                sx={{ fontSize: 13, borderRadius: '8px' }}
              >
                <MenuItem value="" sx={{ fontSize: 13 }}>All Years</MenuItem>
                {yearOptions.map(y => (
                  <MenuItem key={y} value={y} sx={{ fontSize: 13 }}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 240, bgcolor: 'white', borderRadius: '8px' }}>
              <Select
                displayEmpty
                value={filterMonth}
                onChange={(e) => {
                  setFilterMonth(e.target.value);
                  if (e.target.value) setExpandedMonth(e.target.value);
                }}
                sx={{ fontSize: 13, borderRadius: '8px' }}
              >
                <MenuItem value="" sx={{ fontSize: 13 }}>All Payrolls</MenuItem>
                {filteredMonthOptions.map(opt => (
                  <MenuItem key={opt.run_id} value={opt.run_id} sx={{ fontSize: 13 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" alignItems="center" gap={0.6}
              sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: 'white', border: '1.5px solid #E8EDF5' }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#3B82F6' }} />
              <Typography fontSize={12} fontWeight={600} color="text.secondary">
                {filteredMonths.length} Months
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filteredMonths.length === 0 ? (
          <Paper sx={{ py: 8, textAlign: 'center', border: '1.5px solid #E5E7EB', borderRadius: '12px' }} elevation={0}>
            <InboxIcon sx={{ fontSize: 40, color: '#D1D5DB', mb: 1 }} />
            <Typography fontWeight={600} color="#374151">{search ? 'No matches found' : 'No balance records available'}</Typography>
          </Paper>
        ) : (
          filteredMonths.map(m => {
            const isMonthExpanded = expandedMonth === m.monthId || filterMonth === m.monthId;
            
            return (
              <Box key={m.monthId} sx={{ 
                bgcolor: 'white', borderRadius: '12px', border: '1.5px solid #E5E7EB',
                boxShadow: isMonthExpanded ? '0 4px 12px rgba(0,0,0,0.03)' : 'none',
                overflow: 'hidden', transition: 'all 0.2s'
              }}>
                <Box
                  onClick={() => setExpandedMonth(isMonthExpanded && !filterMonth ? null : m.monthId)}
                  sx={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
                    px: 3, py: 2, cursor: filterMonth ? 'default' : 'pointer',
                    bgcolor: isMonthExpanded ? '#F8FAFC' : 'white',
                    borderBottom: isMonthExpanded ? '1px solid #E5E7EB' : 'none',
                    '&:hover': { bgcolor: filterMonth ? '#F8FAFC' : '#F1F5F9' },
                  }}
                >
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '12px',
                    bgcolor: '#EFF6FF', color: '#3B82F6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CalendarMonthIcon sx={{ fontSize: 22 }} />
                  </Box>
                  
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Typography fontSize={18} fontWeight={700} color="#0F172A">{m.monthLabel}</Typography>
                      <Chip label="Payroll Month" size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 600 }} />
                    </Stack>
                    <Typography fontSize={12} color="#64748B" mt={0.5}>Balance values for {m.monthLabel}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                      <Typography fontSize={10} color="#64748B" fontWeight={600} textTransform="uppercase">Staff</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#0F172A">{m.stats.activeEmployees} Active</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#F0FDF4', borderRadius: '8px', border: '1px solid #DCFCE7' }}>
                      <Typography fontSize={10} color="#16A34A" fontWeight={600} textTransform="uppercase">Gross Pay</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#15803D">{fmtCurrency(m.stats.totalGross)}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#FEF2F2', borderRadius: '8px', border: '1px solid #FEE2E2' }}>
                      <Typography fontSize={10} color="#EF4444" fontWeight={600} textTransform="uppercase">Deductions</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#B91C1C">{fmtCurrency(m.stats.totalDed)}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#EFF6FF', borderRadius: '8px', border: '1px solid #DBEAFE' }}>
                      <Typography fontSize={10} color="#2563EB" fontWeight={600} textTransform="uppercase">Net Pay</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#1D4ED8">{fmtCurrency(m.stats.totalNet)}</Typography>
                    </Box>
                  </Box>

                  {!filterMonth && (
                    <IconButton size="small" sx={{ ml: 1, color: '#3B82F6' }}>
                      {isMonthExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  )}
                </Box>

                <Collapse in={isMonthExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ p: 2, bgcolor: '#F8FAFC' }}>
                    <Stack spacing={2}>
                      {m.employees.map(emp => {
                        const isEmpExpanded = expandedEmployees[`${m.monthId}_${emp.eKey}`];
                        
                        return (
                          <Card key={emp.eKey} elevation={0} sx={{
                            border: '1.5px solid', borderColor: isEmpExpanded ? '#8B5CF6' : '#E2E8F0',
                            borderRadius: '12px', transition: 'all 0.2s',
                            boxShadow: isEmpExpanded ? '0 4px 12px rgba(139,92,246,0.08)' : '0 1px 2px rgba(0,0,0,0.02)'
                          }}>
                            <Box
                              onClick={() => toggleEmployee(m.monthId, emp.eKey)}
                              sx={{
                                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
                                px: 2.5, py: 2, cursor: 'pointer',
                                bgcolor: 'white', borderRadius: '12px',
                                '&:hover': { bgcolor: '#F8FAFC' }
                              }}
                            >
                              <Box sx={{
                                width: 40, height: 40, borderRadius: '50%',
                                background: 'linear-gradient(135deg,#8B5CF6,#6366F1)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                              }}>
                                <Typography fontWeight={700} fontSize={14} color="white">
                                  {emp.fullName.substring(0, 2).toUpperCase()}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ flex: 1, minWidth: 200 }}>
                                <Stack direction="row" alignItems="center" gap={1.5} mb={0.5}>
                                  <Typography fontSize={15} fontWeight={700} color="#0F172A">{emp.fullName}</Typography>
                                  <Chip label={emp.assignment_id} size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 600 }} />
                                </Stack>
                              </Box>
                              
                              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography fontSize={11} color="#64748B" fontWeight={600} mb={0.2} textTransform="uppercase">Net Pay</Typography>
                                  <Typography fontSize={14} fontWeight={700} color="#2563EB">{fmtCurrency(emp.total_net)}</Typography>
                                </Box>
                                <IconButton size="small" sx={{ color: '#8B5CF6' }}>
                                  {isEmpExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                              </Box>
                            </Box>
                            
                            <Collapse in={isEmpExpanded} unmountOnExit>
                              <Divider />
                              <Box sx={{ px: 2.5, pb: 2.5, pt: 1, bgcolor: 'white' }}>
                                <Box sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 60px',
                                  gap: 2, px: 2, py: 1.5,
                                  bgcolor: '#F1F5F9', borderRadius: '8px', mb: 1
                                }}>
                                  <Typography fontSize={12} fontWeight={700} color="#475569">Balance Name</Typography>
                                  <Typography fontSize={12} fontWeight={700} color="#475569">Defined Balance</Typography>
                                  <Typography fontSize={12} fontWeight={700} color="#475569">Fiscal Year</Typography>
                                  <Typography fontSize={12} fontWeight={700} color="#475569">Last Updated</Typography>
                                  <Typography fontSize={12} fontWeight={700} color="#475569" textAlign="right">Balance Amount</Typography>
                                  <Typography fontSize={12} fontWeight={700} color="#475569" textAlign="center">Actions</Typography>
                                </Box>
                                
                                {emp.entries.map((entry, idx) => {
                                  const db = definedBalMap[entry.defined_balance_id];
                                  const bDef = db ? balanceDefMap[db.balance_def_id] : null;
                                  const bName = bDef ? bDef.balance_name : (db ? db.dimension_name : entry.defined_balance_id || '—');
                                  
                                  return (
                                    <Box key={idx} sx={{
                                      display: 'grid',
                                      gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 60px',
                                      gap: 2, px: 2, py: 1.5,
                                      borderBottom: idx === emp.entries.length - 1 ? 'none' : '1px solid #F1F5F9',
                                      alignItems: 'center',
                                      '&:hover': { bgcolor: '#F8FAFC' }
                                    }}>
                                      <Typography fontSize={13} color="#334155" fontWeight={500}>{bName}</Typography>
                                      <Typography fontSize={13} color="#64748B">{db ? db.dimension_name : '—'}</Typography>
                                      <Typography fontSize={13} color="#64748B">{entry.fiscal_year || '—'}</Typography>
                                      <Typography fontSize={13} color="#64748B">{entry.last_updated ? new Date(entry.last_updated).toLocaleDateString() : '—'}</Typography>
                                      <Typography fontSize={13} fontWeight={600} color="#0F172A" textAlign="right">
                                        {fmtCurrency(entry.balance_amount, entry.currency)}
                                      </Typography>
                                      <Stack direction="row" spacing={0.5} justifyContent="center">
                                        {onView && (
                                          <IconButton size="small" onClick={() => onView(entry)} sx={{ color: '#3B82F6', '&:hover': { bgcolor: '#EFF6FF' } }}>
                                            <VisibilityIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        )}
                                        {onEdit && (
                                          <IconButton size="small" onClick={() => onEdit(entry)} sx={{ color: '#8B5CF6', '&:hover': { bgcolor: '#F5F3FF' } }}>
                                            <EditIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        )}
                                      </Stack>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Collapse>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
